import React, { useMemo } from 'react';
import { Platform, View } from 'react-native';
import Svg, {
  Circle,
  ClipPath,
  Defs,
  Ellipse,
  G,
  LinearGradient,
  Path,
  RadialGradient,
  Stop,
} from 'react-native-svg';

import type { AIPresenceState } from '@/common/components/presence/types';
import {
  buildFormationContourPath,
  buildFormationLobeSpecs,
  hexToRgba,
  liftNebulaRgb,
  mixNebulaRgb,
  parseNebulaRgb,
} from '@/common/components/presence/nebula/nebulaContourGeometry';
import {
  buildNebulaVisualFrame,
  getNebulaFormationPaintRgb,
  nebulaWellAtmosphereRgb,
  NEBULA_STATIC_PHASE_MS,
} from '@/common/components/presence/nebula/nebulaVisualModel';
import type { OrbPaletteStops } from '@/common/theme/orbPalette';

type NebulaOrbStaticProps = {
  palette: OrbPaletteStops;
  state: AIPresenceState;
  size: number;
};

const shellStyle = {
  position: 'relative' as const,
  overflow: 'hidden' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
};

/**
 * Intentional static Deep-Field Nebula composition for reduced motion and native.
 * SVG irregular paths + tenant gradients at a fixed phase — no canvas or RAF.
 */
export function NebulaOrbStatic({ palette, state, size }: NebulaOrbStaticProps) {
  const frame = useMemo(
    () => buildNebulaVisualFrame({
      state,
      size,
      elapsedMs: NEBULA_STATIC_PHASE_MS,
    }),
    [state, size],
  );

  const cx = size / 2;
  const cy = size / 2;
  const radius = frame.silhouetteRadius;
  const well = parseNebulaRgb(palette.well);
  const atmosphere = nebulaWellAtmosphereRgb(palette, frame.luminosity);
  const liftedWell = mixNebulaRgb(well, atmosphere, frame.wellColorLift);
  const wellHighlight = mixNebulaRgb(
    well,
    liftNebulaRgb(atmosphere, 0.16),
    Math.min(1, frame.wellColorLift + 0.18),
  );
  const wellDeep = mixNebulaRgb(well, [0, 0, 0], 0.42);
  const wellCx = cx + (frame.wellCenterX - 0.5) * radius * 0.9;
  const wellCy = cy + (frame.wellCenterY - 0.5) * radius * 0.9;
  const rim = parseNebulaRgb(palette.rim);

  return (
    <View
      style={[shellStyle, { width: size, height: size, borderRadius: radius }]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      testID="nebula-orb-static"
    >
      <Svg width={size} height={size} testID="nebula-static-svg">
        <Defs>
          <ClipPath id="nebula-static-clip">
            <Circle cx={cx} cy={cy} r={radius} testID="nebula-static-clip" />
          </ClipPath>

          <RadialGradient
            id="nebula-static-well-gradient"
            cx={wellCx}
            cy={wellCy}
            rx={radius}
            ry={radius}
            gradientUnits="userSpaceOnUse"
          >
            <Stop
              offset="0"
              stopColor={`rgb(${wellHighlight.join(',')})`}
              stopOpacity={frame.wellAlpha}
            />
            <Stop
              offset="0.58"
              stopColor={`rgb(${liftedWell.join(',')})`}
              stopOpacity={frame.wellAlpha}
            />
            <Stop offset="1" stopColor={`rgb(${wellDeep.join(',')})`} stopOpacity={1} />
          </RadialGradient>

          {frame.formations.map((formation) => {
            const color = liftNebulaRgb(
              getNebulaFormationPaintRgb(palette[formation.cloudFamily]),
              frame.cloudColorLift,
            );
            const highlight = liftNebulaRgb(color, 0.18);
            const shadow = color.map((channel) => Math.round(channel * 0.58));
            const px = cx + formation.centerX * radius;
            const py = cy + formation.centerY * radius;
            const baseRx = formation.scaleX * radius * 0.67;
            const baseRy = formation.scaleY * radius * 0.67;
            const lobes = buildFormationLobeSpecs(formation, radius, size);
            return (
              <React.Fragment key={`grad-${formation.id}`}>
                <RadialGradient
                  id={`nebula-static-formation-gradient-${formation.id}`}
                  cx={px}
                  cy={py}
                  rx={Math.max(baseRx, baseRy)}
                  ry={Math.max(baseRx, baseRy)}
                  gradientUnits="userSpaceOnUse"
                >
                  <Stop offset="0" stopColor={`rgb(${highlight.join(',')})`} stopOpacity={formation.alpha * 0.5} />
                  <Stop offset="0.3" stopColor={`rgb(${color.join(',')})`} stopOpacity={formation.alpha * 0.4} />
                  <Stop offset="0.62" stopColor={`rgb(${shadow.join(',')})`} stopOpacity={formation.alpha * 0.2} />
                  <Stop offset="1" stopColor={`rgb(${color.join(',')})`} stopOpacity={0} />
                </RadialGradient>
                {lobes.map((lobe, index) => {
                  const lobeCx = px + lobe.ox;
                  const lobeCy = py + lobe.oy;
                  const lobeColor = index % 4 === 0
                    ? highlight
                    : index % 4 === 2
                      ? shadow
                      : color;
                  return (
                    <RadialGradient
                      key={`lobe-grad-${formation.id}-${lobe.ox}-${lobe.oy}`}
                      id={`nebula-static-lobe-gradient-${formation.id}-${index}`}
                      cx={lobeCx}
                      cy={lobeCy}
                      rx={Math.max(lobe.lrx, lobe.lry)}
                      ry={Math.max(lobe.lrx, lobe.lry)}
                      gradientUnits="userSpaceOnUse"
                    >
                      <Stop offset="0" stopColor={`rgb(${lobeColor.join(',')})`} stopOpacity={lobe.alpha * 0.48} />
                      <Stop offset="0.46" stopColor={`rgb(${color.join(',')})`} stopOpacity={lobe.alpha * 0.26} />
                      <Stop offset="0.76" stopColor={`rgb(${shadow.join(',')})`} stopOpacity={lobe.alpha * 0.08} />
                      <Stop offset="1" stopColor={`rgb(${color.join(',')})`} stopOpacity={0} />
                    </RadialGradient>
                  );
                })}
              </React.Fragment>
            );
          })}

          <RadialGradient
            id="nebula-static-inner-shadow-gradient"
            cx={cx}
            cy={cy}
            rx={radius * 0.88}
            ry={radius * 0.88}
            fx={cx}
            fy={cy}
            gradientUnits="userSpaceOnUse"
          >
            <Stop offset="0" stopColor="#000000" stopOpacity={0} />
            <Stop offset="0.55" stopColor="#000000" stopOpacity={frame.innerShadowStrength * 0.35} />
            <Stop offset="1" stopColor="#000000" stopOpacity={Math.min(frame.innerShadowStrength + 0.12, 0.72)} />
          </RadialGradient>

          <LinearGradient
            id="nebula-static-rim-gradient"
            x1={cx - radius}
            y1={cy - radius}
            x2={cx + radius * 0.6}
            y2={cy + radius}
            gradientUnits="userSpaceOnUse"
          >
            <Stop offset="0" stopColor="#97BECA" stopOpacity={frame.rimAlpha * 0.48} />
            <Stop
              offset="0.5"
              stopColor={`rgb(${rim.join(',')})`}
              stopOpacity={frame.rimAlpha * 0.72}
            />
            <Stop offset="1" stopColor="#5B7A88" stopOpacity={frame.rimAlpha * 0.85} />
          </LinearGradient>
        </Defs>

        {frame.haloAlpha > 0 && Platform.OS === 'web' ? (
          <Circle
            cx={cx}
            cy={cy}
            r={radius * 1.35}
            fill={`rgba(${palette.haloRgb.join(',')},${frame.haloAlpha})`}
            testID="nebula-static-halo"
          />
        ) : null}

        <G clipPath="url(#nebula-static-clip)">
          <Circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="url(#nebula-static-well-gradient)"
            testID="nebula-static-well"
          />

          {frame.formations.map((formation) => {
            const px = cx + formation.centerX * radius;
            const py = cy + formation.centerY * radius;
            const contourPath = buildFormationContourPath(formation, cx, cy, radius);
            const lobes = buildFormationLobeSpecs(formation, radius, size);
            return (
              <G
                key={formation.id}
                testID={`nebula-static-formation-${formation.id}`}
              >
                <Path
                  d={contourPath}
                  fill={`url(#nebula-static-formation-gradient-${formation.id})`}
                  testID={`nebula-static-formation-${formation.id}-contour`}
                />
                {lobes.map((lobe, index) => {
                  const lobeCx = px + lobe.ox;
                  const lobeCy = py + lobe.oy;
                  const lobeRotation = (lobe.rotation * 180) / Math.PI;
                  const lobeKey = `${formation.id}-lobe-${Math.round(lobe.ox * 100)}-${Math.round(lobe.oy * 100)}`;
                  return (
                    <G
                      key={lobeKey}
                      transform={`rotate(${lobeRotation}, ${lobeCx}, ${lobeCy})`}
                    >
                      <Ellipse
                        cx={lobeCx}
                        cy={lobeCy}
                        rx={lobe.lrx}
                        ry={lobe.lry}
                        fill={`url(#nebula-static-lobe-gradient-${formation.id}-${index})`}
                        testID={`nebula-static-formation-${formation.id}-lobe-${Math.round(lobe.ox * 100)}-${Math.round(lobe.oy * 100)}`}
                      />
                    </G>
                  );
                })}
              </G>
            );
          })}

          {frame.coreAlpha > 0.02 ? (
            <G>
              <Circle
                cx={cx}
                cy={cy}
                r={radius * 0.2}
                fill={hexToRgba(palette.core, frame.coreAlpha)}
                testID="nebula-static-core"
              />
            </G>
          ) : null}

          <Circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="url(#nebula-static-inner-shadow-gradient)"
            testID="nebula-static-inner-shadow"
          />
        </G>

        <Circle
          cx={cx}
          cy={cy}
          r={radius - 0.5}
          stroke="url(#nebula-static-rim-gradient)"
          strokeWidth={Math.max(1, size * 0.022)}
          fill="none"
          testID="nebula-static-rim"
        />
      </Svg>
    </View>
  );
}
